import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Megaphone, Tag, ArrowRight, Calendar } from "lucide-react";

export default function Offers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/promotions").then(({ data }) => {
      setItems(data);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      <div className="overline text-gray-500 inline-flex items-center gap-1.5">
        <Megaphone size={12} /> Special offers
      </div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
        Discounts from local businesses.
      </h1>
      <p className="text-gray-600 mt-3 max-w-2xl">
        Active offers sent by Servrevu Pro businesses.{" "}
        <Link to="/register" className="underline text-[#B31E1E]">Register</Link> and we'll message new offers as they go live — turn it off any time from your dashboard.
      </p>

      <div className="mt-10">
        {loading ? (
          <p className="text-gray-500">Loading offers…</p>
        ) : items.length === 0 ? (
          <Card className="rounded-md border border-border p-10 text-center" data-testid="offers-empty">
            <p className="text-gray-500">No active offers right now. Check back soon.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="offers-grid">
            {items.map((p) => (
              <Card
                key={p.id}
                className="rounded-md border border-border p-6 hover:shadow-md hover:-translate-y-px transition-all flex flex-col"
                data-testid={`offer-${p.id}`}
              >
                <div className="flex items-start gap-3">
                  {p.business_logo ? (
                    <img
                      src={p.business_logo}
                      alt={`${p.business_name} logo`}
                      className="h-12 w-12 rounded-md border border-border object-contain bg-white shrink-0"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-md border border-border bg-gray-50 flex items-center justify-center text-[11px] font-mono text-gray-300 shrink-0">
                      {p.business_name?.[0] || "?"}
                    </div>
                  )}
                  <div className="min-w-0">
                    <Link to={`/businesses/${p.business_id}`} className="text-sm text-[#B31E1E] hover:underline truncate block">
                      {p.business_name}
                    </Link>
                    <h3 className="font-display text-xl mt-0.5 leading-tight">{p.title}</h3>
                  </div>
                </div>

                <p className="mt-4 text-sm text-gray-700 leading-relaxed line-clamp-4 flex-1">
                  {p.description}
                </p>

                {p.discount_code && (
                  <div className="mt-4 inline-flex items-center gap-2 self-start">
                    <Badge
                      variant="outline"
                      className="rounded-md font-mono text-[12px] tracking-widest border-dashed border-[#B31E1E] text-[#B31E1E] px-3 py-1.5 bg-[#B31E1E]/5"
                      data-testid={`offer-code-${p.id}`}
                    >
                      <Tag size={11} className="mr-1" /> {p.discount_code}
                    </Badge>
                  </div>
                )}

                <div className="mt-5 pt-4 border-t border-border flex items-center justify-between text-xs">
                  <span className="font-mono text-gray-500 inline-flex items-center gap-1">
                    <Calendar size={11} /> Expires {p.expires_at}
                  </span>
                  <Link
                    to={`/businesses/${p.business_id}`}
                    className="text-[#B31E1E] hover:underline inline-flex items-center gap-1 font-medium"
                    data-testid={`offer-view-${p.id}`}
                  >
                    View business <ArrowRight size={12} />
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
